import React, { useState, useEffect, useMemo } from "react";
import { useTableColumnFilters, FilterTh } from "../components/tableFilters";
import { Order, OrderStatus, OrderUnit, Firm, ModelCost } from "../types";
import {
  orderService,
  firmService,
  costService,
} from "../services/dataService";
import { turkishIncludes, formatCurrency } from "../utils/formatters";
import PageLoader from "../components/PageLoader";
import ImageManager from "../components/ImageManager";
import AlertModal from "../components/AlertModal";
import OrderDetailModal from "../components/OrderDetailModal";
import "./Orders.css";

const STORAGE_KEY = "accounting-orders-filters";

interface CostDetailModal {
  isOpen: boolean;
  orderId: string;
  modelId: string;
  modelCode: string;
  firmCode: string;
  firmName: string;
  quantity: number;
  costs: ModelCost[];
  loading: boolean;
  totalCost: number;
  costWithOverhead: number;
  finalCostWithProfit: number;
}

// localStorage'dan filtreleri yükle
const loadFiltersFromStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        firmFilter: parsed.firmFilter || [],
        dateFilter: parsed.dateFilter || { startDate: "", endDate: "" },
        searchTerm: parsed.searchTerm || "",
        invoiceFilter: parsed.invoiceFilter || "all",
      };
    }
  } catch (error) {
    console.error("Failed to load filters from storage:", error);
  }
  return {
    firmFilter: [],
    dateFilter: { startDate: "", endDate: "" },
    searchTerm: "",
    invoiceFilter: "all" as "all" | "filled" | "empty",
  };
};

const AccountingOrders: React.FC = () => {
  const savedFilters = loadFiltersFromStorage();

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>(savedFilters.searchTerm);
  const [invoiceFilter, setInvoiceFilter] = useState<
    "all" | "filled" | "empty"
  >(savedFilters.invoiceFilter);
  const [firmFilter, setFirmFilter] = useState<string[]>(
    savedFilters.firmFilter,
  );
  const [dateFilter, setDateFilter] = useState({
    startDate: savedFilters.dateFilter.startDate || "",
    endDate: savedFilters.dateFilter.endDate || "",
  });
  const [firms, setFirms] = useState<Firm[]>([]);
  const [firmSearch, setFirmSearch] = useState<string>("");
  const [firmDropdownOpen, setFirmDropdownOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<{
    orderId: string;
    invoice: string;
    invoiceNumber: string;
  } | null>(null);
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [showImageManager, setShowImageManager] = useState(false);
  const [selectedOrderForImages, setSelectedOrderForImages] = useState<
    string | null
  >(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // Detail Modal state
  const [detailModal, setDetailModal] = useState<{
    isOpen: boolean;
    orderId: string;
  }>({ isOpen: false, orderId: "" });

  const [costDetailModal, setCostDetailModal] = useState<CostDetailModal>({
    isOpen: false,
    orderId: "",
    modelId: "",
    modelCode: "",
    firmCode: "",
    firmName: "",
    quantity: 0,
    costs: [],
    loading: false,
    totalCost: 0,
    costWithOverhead: 0,
    finalCostWithProfit: 0,
  });

  const openCostDetail = async (order: Order) => {
    setCostDetailModal({
      isOpen: true,
      orderId: order.orderId,
      modelId: order.modelId,
      modelCode: order.model?.modelCode || "",
      firmCode: order.firm?.firmCode || "",
      firmName: order.firm?.firmName || "",
      quantity: order.quantity || 0,
      costs: [],
      loading: true,
      totalCost: 0,
      costWithOverhead: 0,
      finalCostWithProfit: 0,
    });
    try {
      const costs = await costService.getModelCosts(order.orderId);
      const toTRY = (
        amount: number,
        currency: string,
        usdRate?: number,
        eurRate?: number,
        gbpRate?: number,
      ) => {
        if (currency === "TRY" || currency === "TL") return amount;
        if (currency === "USD" && usdRate) return amount * usdRate;
        if (currency === "EUR" && eurRate) return amount * eurRate;
        if (currency === "GBP" && gbpRate) return amount * gbpRate;
        return 0;
      };
      const totalCost = costs.reduce(
        (sum, c) =>
          sum +
          toTRY(
            c.totalCost || 0,
            c.currency || "TRY",
            c.usdRate,
            c.eurRate,
            c.gbpRate,
          ),
        0,
      );
      const costWithOverhead = costs.reduce(
        (sum, c) =>
          sum +
          toTRY(
            c.costWithOverhead || c.totalCost || 0,
            c.currency || "TRY",
            c.usdRate,
            c.eurRate,
            c.gbpRate,
          ),
        0,
      );
      const finalCostWithProfit = costs.reduce(
        (sum, c) =>
          sum +
          toTRY(
            c.finalCostWithProfit || c.totalCost || 0,
            c.currency || "TRY",
            c.usdRate,
            c.eurRate,
            c.gbpRate,
          ),
        0,
      );
      setCostDetailModal((prev) => ({
        ...prev,
        costs,
        loading: false,
        totalCost,
        costWithOverhead,
        finalCostWithProfit,
      }));
    } catch (error) {
      setCostDetailModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const closeCostDetail = () => {
    setCostDetailModal({
      isOpen: false,
      orderId: "",
      modelId: "",
      modelCode: "",
      firmCode: "",
      firmName: "",
      quantity: 0,
      costs: [],
      loading: false,
      totalCost: 0,
      costWithOverhead: 0,
      finalCostWithProfit: 0,
    });
  };

  const convertCostToTRY = (cost: ModelCost): number => {
    const amount = cost.totalCost || 0;
    const currency = cost.currency || "TRY";
    if (currency === "TRY" || currency === "TL") return amount;
    if (currency === "USD" && cost.usdRate) return amount * cost.usdRate;
    if (currency === "EUR" && cost.eurRate) return amount * cost.eurRate;
    if (currency === "GBP" && cost.gbpRate) return amount * cost.gbpRate;
    return 0;
  };

  const convertCostWithOverheadToTRY = (cost: ModelCost): number => {
    const amount = cost.costWithOverhead || 0;
    const currency = cost.currency || "TRY";
    if (currency === "TRY" || currency === "TL") return amount;
    if (currency === "USD" && cost.usdRate) return amount * cost.usdRate;
    if (currency === "EUR" && cost.eurRate) return amount * cost.eurRate;
    if (currency === "GBP" && cost.gbpRate) return amount * cost.gbpRate;
    return 0;
  };

  const convertFinalCostToTRY = (cost: ModelCost): number => {
    const amount = cost.finalCostWithProfit || 0;
    const currency = cost.currency || "TRY";
    if (currency === "TRY" || currency === "TL") return amount;
    if (currency === "USD" && cost.usdRate) return amount * cost.usdRate;
    if (currency === "EUR" && cost.eurRate) return amount * cost.eurRate;
    if (currency === "GBP" && cost.gbpRate) return amount * cost.gbpRate;
    return 0;
  };

  // Fason flag state - her sipariş için maliyetlerde "fason" geçip geçmediğini tutar
  const [fasonFlags, setFasonFlags] = useState<Record<string, boolean | null>>(
    {},
  );

  // Alert Modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "success" | "warning" | "error";
  }>({ isOpen: false, message: "", type: "info" });

  useEffect(() => {
    loadOrders();
    loadFirms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtreleri localStorage'a kaydet
  useEffect(() => {
    const filtersToSave = {
      firmFilter,
      dateFilter,
      searchTerm,
      invoiceFilter,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtersToSave));
  }, [firmFilter, dateFilter, searchTerm, invoiceFilter]);

  // Filtreleri değiştir ve siparişleri yeniden yükle
  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmFilter, dateFilter]);

  // Dropdown'ları dışarıda tıklandığında kapat
  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".searchable-select") &&
        !target.closest(".action-dropdown-container") &&
        !target.closest(".action-dropdown-menu")
      ) {
        setFirmDropdownOpen(false);
        setOpenDropdownId(null);
        setDropdownPosition(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Scroll olduğunda dropdown'u kapat
  useEffect(() => {
    const handleScroll = () => {
      if (openDropdownId) {
        setOpenDropdownId(null);
        setDropdownPosition(null);
      }
    };
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [openDropdownId]);

  // Sipariş maliyetlerinde "fason" geçen siparişleri tara
  const scanFasonFlags = async (orderList: Order[]) => {
    if (orderList.length === 0) return;
    // Hepsini loading olarak işaretle
    const loadingFlags: Record<string, boolean | null> = {};
    orderList.forEach((o) => {
      loadingFlags[o.orderId] = null;
    });
    setFasonFlags(loadingFlags);

    const results = await Promise.allSettled(
      orderList.map(async (order) => {
        const costs = await costService.getModelCosts(order.orderId);
        const hasFason = costs.some((c) => {
          const text = [c.costItemName, c.costCategoryName, c.usage, c.notes]
            .filter(Boolean)
            .join(" ");
          return turkishIncludes(text, "fason");
        });
        return [order.orderId, hasFason] as const;
      }),
    );

    const resolved: Record<string, boolean | null> = {};
    results.forEach((r, i) => {
      if (r.status === "fulfilled") {
        resolved[r.value[0]] = r.value[1];
      } else {
        resolved[orderList[i].orderId] = false;
      }
    });
    setFasonFlags(resolved);
  };

  const loadOrders = async () => {
    try {
      setLoading(true);

      // Tüm siparişleri çek (hem IsActive=true hem IsActive=false)
      const allOrders = await orderService.getInvoiced();

      // Sadece tamamlanmış siparişleri filtrele
      let filteredData = allOrders.filter(
        (order) => order.status === OrderStatus.Tamamlandi,
      );

      // Firma filtresi uygula
      if (firmFilter.length > 0) {
        filteredData = filteredData.filter(
          (order) => order.firmId && firmFilter.includes(order.firmId),
        );
      }

      // Tarih filtresi uygula
      if (dateFilter.startDate || dateFilter.endDate) {
        filteredData = filteredData.filter((order) => {
          if (!order.completionDate) return false;

          const orderDate = new Date(order.completionDate);
          const startDate = dateFilter.startDate
            ? new Date(dateFilter.startDate)
            : null;
          const endDate = dateFilter.endDate
            ? new Date(dateFilter.endDate)
            : null;

          if (startDate && orderDate < startDate) return false;
          if (endDate && orderDate > endDate) return false;

          return true;
        });
      }

      setOrders(filteredData);
      // Fason taramasını başlat (arka planda)
      scanFasonFlags(filteredData);
    } catch (error: any) {
      console.error("❌ Failed to load orders:", error);
      setAlertModal({
        isOpen: true,
        message: "Siparişler yüklenemedi!",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadFirms = async () => {
    try {
      const data = await firmService.getFirms();
      setFirms(data);
    } catch (error) {
      console.error("❌ Failed to load firms:", error);
    }
  };

  const getStatusText = (status?: OrderStatus): string => {
    if (status === undefined || status === null) return "Atanmadı";
    switch (status) {
      case OrderStatus.Atanmadi:
        return "Atanmadı";
      case OrderStatus.Islemde:
        return "İşlemde";
      case OrderStatus.Tamamlandi:
        return "Tamamlandı";
      case OrderStatus.IptalEdildi:
        return "İptal Edildi";
      default:
        return "Atanmadı";
    }
  };

  const getStatusClass = (status?: OrderStatus): string => {
    if (status === undefined) return "status-atanmadi";
    switch (status) {
      case OrderStatus.Atanmadi:
        return "status-atanmadi";
      case OrderStatus.Islemde:
        return "status-islemde";
      case OrderStatus.Tamamlandi:
        return "status-tamamlandi";
      case OrderStatus.IptalEdildi:
        return "status-iptal";
      default:
        return "status-atanmadi";
    }
  };

  const getUnitText = (unit?: OrderUnit): string => {
    const unitMap: { [key: number]: string } = {
      [OrderUnit.Adet]: "Adet",
      [OrderUnit.Metre]: "Metre",
      [OrderUnit.Takim]: "Takım",
      [OrderUnit.Pano]: "Pano",
    };
    return unitMap[unit ?? OrderUnit.Adet] || "Adet";
  };

  // Arama ve fatura filtresi fonksiyonu
  const getFilteredOrders = (ordersToFilter: Order[]) => {
    let filtered = ordersToFilter;

    // Fatura filtresi uygula
    if (invoiceFilter === "filled") {
      filtered = filtered.filter(
        (order) => order.invoiceNumber && order.invoiceNumber.trim() !== "",
      );
    } else if (invoiceFilter === "empty") {
      filtered = filtered.filter(
        (order) => !order.invoiceNumber || order.invoiceNumber.trim() === "",
      );
    }

    // Arama filtresi uygula
    if (!searchTerm.trim()) return filtered;

    return filtered.filter((order) => {
      return (
        turkishIncludes(order.orderId || "", searchTerm) ||
        turkishIncludes(order.firm?.firmName || "", searchTerm) ||
        turkishIncludes(order.model?.modelName || "", searchTerm) ||
        turkishIncludes(order.model?.modelCode || "", searchTerm) ||
        turkishIncludes(order.invoice || "", searchTerm) ||
        turkishIncludes(order.invoiceNumber || "", searchTerm) ||
        turkishIncludes(getStatusText(order.status), searchTerm)
      );
    });
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("tr-TR");
  };

  const handleEditInvoice = (order: Order) => {
    setEditingInvoice({
      orderId: order.orderId,
      invoice: order.invoice || "",
      invoiceNumber: order.invoiceNumber || "",
    });
  };

  const handleSaveInvoice = async () => {
    if (!editingInvoice) return;

    try {
      setSavingInvoice(true);

      // Yeni invoice endpoint'ini kullan
      await orderService.updateInvoice(editingInvoice.orderId, {
        invoice: editingInvoice.invoice,
        invoiceNumber: editingInvoice.invoiceNumber,
      });

      // Listeyi güncelle
      setOrders((prev) =>
        prev.map((o) =>
          o.orderId === editingInvoice.orderId
            ? {
                ...o,
                invoice: editingInvoice.invoice,
                invoiceNumber: editingInvoice.invoiceNumber,
              }
            : o,
        ),
      );

      setEditingInvoice(null);
      setAlertModal({
        isOpen: true,
        message: "✅ Fatura bilgileri kaydedildi!",
        type: "success",
      });
    } catch (error) {
      console.error("❌ Fatura güncellenemedi:", error);
      setAlertModal({
        isOpen: true,
        message: "❌ Fatura bilgileri kaydedilemedi!",
        type: "error",
      });
    } finally {
      setSavingInvoice(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingInvoice(null);
  };

  const processedOrders = getFilteredOrders(orders);

  const aoColDefs = useMemo(
    () => [
      { key: "orderId", value: (o: Order) => o.orderId || "" },
      { key: "firm", value: (o: Order) => o.firm?.firmName || "" },
      { key: "model", value: (o: Order) => o.model?.modelName || "" },
      { key: "quantity", type: "float", value: (o: Order) => o.quantity ?? null },
      { key: "unit", value: (o: Order) => getUnitText(o.unit) },
      { key: "price", type: "decimal", value: (o: Order) => o.price ?? null },
      { key: "total", type: "decimal", value: (o: Order) => (o.price && o.quantity ? o.price * o.quantity : null) },
      { key: "status", value: (o: Order) => getStatusText(o.status) },
      { key: "acceptanceDate", type: "datetime", value: (o: Order) => o.acceptanceDate || null },
      { key: "completionDate", type: "datetime", value: (o: Order) => o.completionDate || null },
      { key: "active", value: (o: Order) => (o.isActive ? "Aktif" : "Pasif") },
      { key: "invoice", value: (o: Order) => o.invoice || "" },
      { key: "invoiceNo", value: (o: Order) => o.invoiceNumber || "" },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const { ctx: aoCf, filtered: displayOrders } = useTableColumnFilters(processedOrders, aoColDefs);

  if (loading) {
    return <PageLoader message="Siparişler yükleniyor..." />;
  }

  return (
    <div className="orders-container">
      {/* Sayfa Başlığı */}
      <div className="orders-header">
        <h1>Fatura Yönetimi</h1>
        <p style={{ color: "#666", fontSize: "14px", marginTop: "8px" }}>
          Siparişlere fatura bilgileri ekleyin
        </p>
      </div>

      {/* Filtreler */}
      <div className="filters">
        <div className="filter-group">
          <label>Arama:</label>
          <input
            type="text"
            placeholder="Order ID, Firma, Model, Fatura ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <label>Firma:</label>
          <div className="searchable-select">
            <div
              className="select-header"
              onClick={() => setFirmDropdownOpen(!firmDropdownOpen)}
            >
              <span>
                {firmFilter.length === 0
                  ? "Tüm Firmalar"
                  : firmFilter.length === 1
                    ? firms.find((f) => f.firmId === firmFilter[0])?.firmName ||
                      "Seçiniz"
                    : `${firmFilter.length} firma seçili`}
              </span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M7 10l5 5 5-5z" />
              </svg>
            </div>
            {firmDropdownOpen && (
              <div className="select-dropdown">
                <input
                  type="text"
                  placeholder="Firma ara..."
                  value={firmSearch}
                  onChange={(e) => setFirmSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
                <div className="select-options">
                  <div
                    className="select-option-checkbox"
                    onClick={() => setFirmFilter([])}
                  >
                    <input
                      type="checkbox"
                      checked={firmFilter.length === 0}
                      readOnly
                    />
                    <span>Tüm Firmalar</span>
                  </div>
                  {firms
                    .filter(
                      (f) =>
                        turkishIncludes(f.firmCode, firmSearch) ||
                        turkishIncludes(f.firmName, firmSearch),
                    )
                    .sort((a, b) => a.firmName.localeCompare(b.firmName))
                    .map((firm) => (
                      <div
                        key={firm.firmId}
                        className="select-option-checkbox"
                        onClick={() => {
                          if (firmFilter.includes(firm.firmId)) {
                            setFirmFilter(
                              firmFilter.filter((id) => id !== firm.firmId),
                            );
                          } else {
                            setFirmFilter([...firmFilter, firm.firmId]);
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={firmFilter.includes(firm.firmId)}
                          readOnly
                        />
                        <span>{firm.firmName}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="filter-group">
          <label>Başlangıç Tarihi:</label>
          <input
            type="date"
            value={dateFilter.startDate}
            onChange={(e) =>
              setDateFilter({ ...dateFilter, startDate: e.target.value })
            }
          />
        </div>

        <div className="filter-group">
          <label>Bitiş Tarihi:</label>
          <input
            type="date"
            value={dateFilter.endDate}
            onChange={(e) =>
              setDateFilter({ ...dateFilter, endDate: e.target.value })
            }
          />
        </div>

        <div className="filter-group">
          <label>Fatura Durumu:</label>
          <select
            value={invoiceFilter}
            onChange={(e) =>
              setInvoiceFilter(e.target.value as "all" | "filled" | "empty")
            }
            className="role-select"
          >
            <option value="all">Tümü</option>
            <option value="filled">Fatura Numarası Girilmiş</option>
            <option value="empty">Fatura Numarası Girilmemiş</option>
          </select>
        </div>

        {/* Filtreleri Temizle Butonu */}
        {(firmFilter.length > 0 ||
          dateFilter.startDate ||
          dateFilter.endDate ||
          searchTerm ||
          invoiceFilter !== "all") && (
          <div className="filter-group">
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setFirmFilter([]);
                setDateFilter({ startDate: "", endDate: "" });
                setSearchTerm("");
                setInvoiceFilter("all");
                localStorage.removeItem(STORAGE_KEY);
              }}
              title="Tüm filtreleri temizle"
            >
              ✕ Filtreleri Temizle
            </button>
          </div>
        )}
      </div>

      {/* Tablo */}
      <div className="orders-table-container">
        <table className="orders-table">
          <thead>
            <tr>
              <FilterTh label="Order ID" colKey="orderId" ctx={aoCf} />
              <FilterTh label="Firma" colKey="firm" ctx={aoCf} />
              <FilterTh label="Model" colKey="model" ctx={aoCf} />
              <th>Fason</th>
              <FilterTh label="Miktar" colKey="quantity" type="float" ctx={aoCf} align="left" />
              <FilterTh label="Birim" colKey="unit" ctx={aoCf} />
              <FilterTh label="Birim Fiyat" colKey="price" type="decimal" ctx={aoCf} align="left" />
              <FilterTh label="Toplam Tutar" colKey="total" type="decimal" ctx={aoCf} align="left" />
              <FilterTh label="Durum" colKey="status" ctx={aoCf} />
              <FilterTh label="Kabul Tarihi" colKey="acceptanceDate" type="datetime" ctx={aoCf} />
              <FilterTh label="Tamamlanma Tarihi" colKey="completionDate" type="datetime" ctx={aoCf} />
              <FilterTh label="IsActive" colKey="active" ctx={aoCf} />
              <FilterTh label="Fatura" colKey="invoice" ctx={aoCf} />
              <FilterTh label="Fatura No" colKey="invoiceNo" ctx={aoCf} />
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {displayOrders.length === 0 ? (
              <tr>
                <td colSpan={15} style={{ textAlign: "center" }}>
                  {searchTerm.trim() || aoCf.hasActive
                    ? "Arama kriterine uygun sipariş bulunamadı."
                    : "Tamamlanmış sipariş bulunmuyor."}
                </td>
              </tr>
            ) : (
              displayOrders.map((order) => (
                <tr key={order.orderId}>
                  <td
                    style={{
                      fontFamily: "monospace",
                      fontSize: "11px",
                      color: "#666",
                      cursor: "pointer",
                    }}
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(order.orderId);
                        setAlertModal({
                          isOpen: true,
                          message: `Order ID kopyalındı: ${order.orderId}`,
                          type: "info",
                        });
                      } catch (err) {
                        const textArea = document.createElement("textarea");
                        textArea.value = order.orderId;
                        textArea.style.position = "fixed";
                        textArea.style.opacity = "0";
                        document.body.appendChild(textArea);
                        textArea.select();
                        try {
                          document.execCommand("copy");
                          setAlertModal({
                            isOpen: true,
                            message: `Order ID kopyalındı: ${order.orderId}`,
                            type: "info",
                          });
                        } catch (e) {
                          setAlertModal({
                            isOpen: true,
                            message: "Kopyalama başarısız oldu.",
                            type: "error",
                          });
                        } finally {
                          document.body.removeChild(textArea);
                        }
                      }
                    }}
                    title="Kopyalamak için tıklayın"
                  >
                    {order.orderId.substring(0, 8)}...
                  </td>
                  <td>{order.firm?.firmName || "-"}</td>
                  <td>{order.model?.modelName || "-"}</td>
                  <td className="fason-cell">
                    {fasonFlags[order.orderId] === null ? (
                      <span className="fason-loading">...</span>
                    ) : fasonFlags[order.orderId] ? (
                      <span
                        className="fason-yes"
                        title="Sipariş maliyetlerinde fason kaydı var"
                      >
                        ✓ Fason
                      </span>
                    ) : (
                      <span className="fason-no">-</span>
                    )}
                  </td>
                  <td>{order.quantity || 0}</td>
                  <td>
                    <span className="unit-badge">
                      {getUnitText(order.unit)}
                    </span>
                  </td>
                  <td>
                    {order.price ? (
                      <span style={{ fontWeight: "600" }}>
                        {order.price.toLocaleString("tr-TR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{" "}
                        {order.priceCurrency || order.currency || "TRY"}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    {order.price && order.quantity ? (
                      <span style={{ fontWeight: "600", color: "#28a745" }}>
                        {(order.price * order.quantity).toLocaleString(
                          "tr-TR",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}{" "}
                        {order.priceCurrency || order.currency || "TRY"}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    <span
                      className={`status-badge ${getStatusClass(order.status)}`}
                    >
                      {getStatusText(order.status)}
                    </span>
                  </td>
                  <td>{formatDate(order.acceptanceDate)}</td>
                  <td>{formatDate(order.completionDate)}</td>
                  <td>
                    <span
                      style={{
                        color: order.isActive ? "#28a745" : "#dc3545",
                        fontWeight: "600",
                      }}
                    >
                      {order.isActive ? "✓ Aktif" : "✕ Pasif"}
                    </span>
                  </td>
                  <td>
                    {editingInvoice?.orderId === order.orderId ? (
                      <input
                        type="text"
                        value={editingInvoice.invoice}
                        onChange={(e) =>
                          setEditingInvoice({
                            ...editingInvoice,
                            invoice: e.target.value,
                          })
                        }
                        placeholder="Fatura"
                        style={{ width: "100px", padding: "4px" }}
                      />
                    ) : (
                      <span>{order.invoice || "-"}</span>
                    )}
                  </td>
                  <td>
                    {editingInvoice?.orderId === order.orderId ? (
                      <input
                        type="text"
                        value={editingInvoice.invoiceNumber}
                        onChange={(e) =>
                          setEditingInvoice({
                            ...editingInvoice,
                            invoiceNumber: e.target.value,
                          })
                        }
                        placeholder="Fatura No"
                        style={{ width: "100px", padding: "4px" }}
                      />
                    ) : (
                      <span>{order.invoiceNumber || "-"}</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      {editingInvoice?.orderId === order.orderId ? (
                        <>
                          <button
                            onClick={handleSaveInvoice}
                            disabled={savingInvoice}
                            className="btn btn-icon btn-sm btn-secondary"
                            title="Kaydet"
                            style={{ backgroundColor: "#28a745" }}
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={savingInvoice}
                            className="btn btn-icon btn-sm btn-soft-danger"
                            title="İptal"
                          >
                            ✕
                          </button>
                        </>
                      ) : (
                        <div className="action-dropdown-container">
                          <button
                            className="action-dropdown-toggle"
                            onClick={(e) => {
                              e.stopPropagation();
                              const isOpening =
                                openDropdownId !== order.orderId;
                              if (isOpening) {
                                const rect =
                                  e.currentTarget.getBoundingClientRect();
                                const menuHeight = 200;
                                const spaceBelow =
                                  window.innerHeight - rect.bottom;
                                const shouldOpenUp = spaceBelow < menuHeight;

                                setDropdownPosition({
                                  top: shouldOpenUp
                                    ? rect.top - menuHeight
                                    : rect.bottom + 4,
                                  left: rect.right - 200,
                                });
                                setOpenDropdownId(order.orderId);
                              } else {
                                setOpenDropdownId(null);
                                setDropdownPosition(null);
                              }
                            }}
                            title="Aksiyonlar"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          </button>

                          {openDropdownId === order.orderId &&
                            dropdownPosition && (
                              <div
                                className="action-dropdown-menu"
                                style={{
                                  top: dropdownPosition.top,
                                  left: dropdownPosition.left,
                                }}
                              >
                                <button
                                  className="action-dropdown-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdownId(null);
                                    setDropdownPosition(null);
                                    setDetailModal({
                                      isOpen: true,
                                      orderId: order.orderId,
                                    });
                                  }}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                    <circle cx="12" cy="12" r="3"></circle>
                                  </svg>
                                  <span>Detay</span>
                                </button>
                                <button
                                  className="action-dropdown-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdownId(null);
                                    setDropdownPosition(null);
                                    openCostDetail(order);
                                  }}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <line x1="12" y1="1" x2="12" y2="23"></line>
                                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                                  </svg>
                                  <span>Maliyet Detayları</span>
                                </button>
                                <button
                                  className="action-dropdown-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdownId(null);
                                    setDropdownPosition(null);
                                    handleEditInvoice(order);
                                  }}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                  </svg>
                                  <span>Fatura Düzenle</span>
                                </button>
                                <button
                                  className="action-dropdown-item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenDropdownId(null);
                                    setDropdownPosition(null);
                                    setSelectedOrderForImages(order.orderId);
                                    setShowImageManager(true);
                                  }}
                                >
                                  <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <rect
                                      x="3"
                                      y="3"
                                      width="18"
                                      height="18"
                                      rx="2"
                                      ry="2"
                                    ></rect>
                                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                    <polyline points="21 15 16 10 5 21"></polyline>
                                  </svg>
                                  <span>Resimleri Görüntüle</span>
                                </button>
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showImageManager && selectedOrderForImages && (
        <ImageManager
          orderId={selectedOrderForImages}
          onClose={() => {
            setShowImageManager(false);
            setSelectedOrderForImages(null);
          }}
        />
      )}

      {/* Order Detail Modal */}
      <OrderDetailModal
        isOpen={detailModal.isOpen}
        orderId={detailModal.orderId}
        onClose={() => setDetailModal({ isOpen: false, orderId: "" })}
      />

      {/* Maliyet Detay Modalı */}
      {costDetailModal.isOpen && (
        <div className="cost-detail-modal-overlay">
          <div className="cost-detail-modal">
            <div className="cost-detail-header">
              <div className="cost-detail-title">
                <h2>Maliyet Detayları</h2>
                <div className="cost-detail-info">
                  <span className="info-badge firm">
                    {costDetailModal.firmName}
                  </span>
                  <span className="info-badge model">
                    Model: {costDetailModal.modelCode}
                  </span>
                  <span className="info-badge quantity">
                    Adet: {costDetailModal.quantity}
                  </span>
                </div>
              </div>
              <button className="cost-detail-close" onClick={closeCostDetail}>
                ×
              </button>
            </div>

            <div className="cost-detail-body">
              {costDetailModal.loading ? (
                <div className="cost-detail-loading">
                  <div className="spinner"></div>
                  <p>Maliyet kalemleri yükleniyor...</p>
                </div>
              ) : costDetailModal.costs.length === 0 ? (
                <div className="cost-detail-empty">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#ccc"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4M12 8h.01" />
                  </svg>
                  <p>Bu sipariş için maliyet kalemi bulunamadı.</p>
                </div>
              ) : (
                <>
                  {(() => {
                    const groupedByWorkshop = costDetailModal.costs.reduce(
                      (acc, cost) => {
                        const workshopName =
                          cost.order?.workshop?.name || cost.usage || "Genel";
                        if (!acc[workshopName]) acc[workshopName] = [];
                        acc[workshopName].push(cost);
                        return acc;
                      },
                      {} as Record<string, typeof costDetailModal.costs>,
                    );
                    const workshopNames = Object.keys(groupedByWorkshop);
                    return (
                      <div className="cost-detail-groups">
                        {workshopNames.map((workshopName) => {
                          const costs = groupedByWorkshop[workshopName];
                          const workshopTotal = costs.reduce(
                            (sum, c) => sum + convertCostToTRY(c),
                            0,
                          );
                          return (
                            <div
                              key={workshopName}
                              className="cost-detail-group"
                            >
                              <div className="cost-group-header">
                                <div className="cost-group-title">
                                  <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <rect
                                      x="2"
                                      y="7"
                                      width="20"
                                      height="14"
                                      rx="2"
                                      ry="2"
                                    />
                                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                                  </svg>
                                  <span>{workshopName}</span>
                                </div>
                                <div className="cost-group-total">
                                  {formatCurrency(workshopTotal, "TRY")}
                                </div>
                              </div>
                              <div className="cost-detail-table-wrapper">
                                <table className="cost-detail-table">
                                  <thead>
                                    <tr>
                                      <th>Maliyet Kalemi</th>
                                      <th>Kategori</th>
                                      <th className="text-center">Miktar</th>
                                      <th>Birim</th>
                                      <th className="text-center">Fire %</th>
                                      <th className="text-right">
                                        Birim Fiyat
                                      </th>
                                      <th className="text-right">Toplam</th>
                                      <th className="text-right">
                                        TL Karşılığı
                                      </th>
                                      <th>Detay/Not</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {costs.map((cost) => {
                                      const tryAmount = convertCostToTRY(cost);
                                      return (
                                        <tr key={cost.modelCostId}>
                                          <td>
                                            <strong>
                                              {cost.costItemName || "-"}
                                            </strong>
                                          </td>
                                          <td>
                                            <span className="category-badge-small">
                                              {cost.costCategoryName || "-"}
                                            </span>
                                          </td>
                                          <td className="text-center">
                                            {cost.quantity}
                                            {cost.quantity2 &&
                                              ` × ${cost.quantity2}`}
                                          </td>
                                          <td>
                                            {cost.unit}
                                            {cost.unit2 && ` × ${cost.unit2}`}
                                          </td>
                                          <td className="text-center">
                                            {cost.wastagePercentage
                                              ? `%${cost.wastagePercentage}`
                                              : "-"}
                                          </td>
                                          <td className="text-right">
                                            {formatCurrency(
                                              cost.unitPrice || 0,
                                              cost.currency || "TRY",
                                            )}
                                          </td>
                                          <td className="text-right">
                                            <strong>
                                              {formatCurrency(
                                                cost.totalCost || 0,
                                                cost.currency || "TRY",
                                              )}
                                            </strong>
                                          </td>
                                          <td className="text-right">
                                            <span className="try-value">
                                              {formatCurrency(tryAmount, "TRY")}
                                            </span>
                                            {cost.currency !== "TRY" &&
                                              (cost.usdRate ||
                                                cost.eurRate) && (
                                                <div
                                                  style={{
                                                    fontSize: "11px",
                                                    color: "#666",
                                                    marginTop: "4px",
                                                  }}
                                                >
                                                  Kur:{" "}
                                                  {cost.currency === "USD"
                                                    ? cost.usdRate?.toFixed(4)
                                                    : cost.currency === "EUR"
                                                      ? cost.eurRate?.toFixed(4)
                                                      : "-"}{" "}
                                                  TL
                                                </div>
                                              )}
                                          </td>
                                          <td>
                                            {cost.notes || cost.usage ? (
                                              <div style={{ fontSize: "12px" }}>
                                                {cost.notes && (
                                                  <div
                                                    style={{
                                                      color: "#666",
                                                      fontStyle: "italic",
                                                    }}
                                                  >
                                                    {cost.notes}
                                                  </div>
                                                )}
                                              </div>
                                            ) : (
                                              <span style={{ color: "#999" }}>
                                                -
                                              </span>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          );
                        })}
                        <div className="cost-detail-grand-total">
                          <div className="grand-total-row">
                            <span className="grand-total-label">
                              GENEL TOPLAM
                            </span>
                            <span className="grand-total-value">
                              {formatCurrency(costDetailModal.totalCost, "TRY")}
                            </span>
                          </div>
                          <div
                            className="grand-total-row"
                            style={{
                              background: "#f8f9fe",
                              borderTop: "1px solid #e0e7ff",
                            }}
                          >
                            <span
                              className="grand-total-label"
                              style={{ color: "#667eea" }}
                            >
                              GENEL GİDER DAHİL
                            </span>
                            <span
                              className="grand-total-value"
                              style={{ color: "#667eea" }}
                            >
                              {formatCurrency(
                                costDetailModal.costWithOverhead,
                                "TRY",
                              )}
                            </span>
                          </div>
                          <div
                            className="grand-total-row"
                            style={{
                              background: "#f8f9fe",
                              borderTop: "1px solid #e0e7ff",
                            }}
                          >
                            <span
                              className="grand-total-label"
                              style={{ color: "#764ba2" }}
                            >
                              KAR DAHİL (NİHAİ MALİYET)
                            </span>
                            <span
                              className="grand-total-value"
                              style={{ color: "#764ba2" }}
                            >
                              {formatCurrency(
                                costDetailModal.finalCostWithProfit,
                                "TRY",
                              )}
                            </span>
                          </div>
                          {costDetailModal.quantity > 0 && (
                            <div className="grand-total-row unit">
                              <span className="grand-total-label">
                                BİRİM MALİYET
                              </span>
                              <span className="grand-total-value">
                                {formatCurrency(
                                  costDetailModal.costWithOverhead /
                                    costDetailModal.quantity,
                                  "TRY",
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>

            <div className="cost-detail-footer">
              <button className="close-btn" onClick={closeCostDetail}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
      />
    </div>
  );
};

export default AccountingOrders;
